export type DecisionStatus = "processing" | "approved" | "declined" | "needs_review" | "failed";

export type UnderwritingDataSource = "statements" | "plaid";

export interface UnderwritingDecision {
  id: number;
  borrower_id: number;
  data_source?: UnderwritingDataSource;
  decision_status: DecisionStatus;
  decision_reason: string;
  llm_analysis?: string;
  llm_model?: string;
  llm_analysis_data?: Record<string, any>;
  book_summary?: Record<string, any>;
  risk_score?: number;
  risk_factors?: string[];
  detailed_results?: DecisionDetails;
  rule_details?: RuleDetail[];
  hard_decline_reasons?: string[];

  // Offer details (flattened from backend)
  offer_amount?: number;
  daily_payment?: number;
  weekly_payment?: number;
  term_days?: number;
  factor_rate?: number;
  payback_amount?: number;

  // Revenue analysis
  average_monthly_revenue?: number;
  true_monthly_revenue?: number;

  // MCA position
  current_mca_payout?: number;
  current_positions?: number;

  // Metadata
  processing_time?: number;
  /** When the analysis behind this decision finished; cleared while one re-runs. */
  processing_completed_at?: string | null;
  version?: number;
  created_at: string;
  updated_at: string;
  borrower?: {
    id: number;
    company_name: string;
    owner_name: string;
  };
}

export interface DecisionDetails {
  hard_decline_reasons?: string[];
  rule_details?: RuleDetail[];
  offer?: OfferDetails;
  revenue_analysis?: RevenueAnalysis;
  balance_analysis?: BalanceAnalysis;
  mca_analysis?: MCAAnalysis;

  /** Present on legacy MoneyThumb-produced decisions only. */
  moneythumb_summary?: MoneyThumbSummary;
  /** Legacy Ocrolus alias kept for old decision payloads. */
  ocrolus_summary?: MoneyThumbSummary;

  // Leverage data
  current_leverage_percentage?: number;
  add_up_to_percentage?: number;

  // Offer structure
  offer_factor?: number;
  offer_term_days?: number;

  // LLM analysis result
  llm_result?: LLMAnalysisResult;

  // Structured analysis output for frontend rendering
  analysis?: AnalysisOutput;

  /**
   * Stamped by the AI analysis pipeline when it materializes a run into this
   * decision. `claude` is the historical pipeline stamp; `openrouter` is the
   * current one. Absent on legacy MoneyThumb-produced decisions.
   */
  analysis_engine?: "claude" | "openrouter";
  analysis_run_id?: number;
}

export interface MoneyThumbSummary {
  monthly_revenue?: number;
  true_monthly_revenue?: number;
  average_monthly_net_revenue?: number;
  current_mca_payments?: number;
  current_leverage?: number;
  bounced_payments?: number;
  negative_balance_days?: number;
  low_balance_days?: number;
  days_negative?: number;
  nsf_count?: number;
  months_of_history?: number;
  recent_positions_count?: number;
  payment_history?: {
    months_of_history?: number;
    monthly_revenues?: number[];
  };
  moneythumb_scorecard?: Record<string, any>;
}

export interface LLMAnalysisResult {
  lenders?: LenderPosition[];
  monthly_revenue?: Record<string, number>;
  payout?: number;
  model?: string;
  edge_cases_detected?: string[];
  hard_decline_reasons?: string[];
}

export interface LenderPosition {
  name: string;
  periodic_payment?: number;
  payment_frequency?: "day" | "week" | "month" | null;
  last_payment_date?: string;
  is_satisfied?: boolean;
  funding_amount?: number;
  funding_date?: string;
  status?: "active" | "pif" | "renewed" | "renewal";
  payments?: Array<{
    date: { month: string; day: number; year: number };
    amount: number;
  }>;
}

export interface RuleDetail {
  rule: string;
  rule_name?: string; // Legacy alias
  status: "pass" | "decline" | "warning";
  passed?: boolean; // Legacy alias
  reason: string;
  message?: string; // Legacy alias
  details?: Record<string, any>;
  data?: Record<string, any>; // Legacy alias
}

export interface OfferDetails {
  max_offer_amount?: number;
  offer_amount?: number;
  daily_payment?: number;
  weekly_payment?: number;
  term_days?: number;
  factor_rate?: number;
  payback_amount?: number;

  current_leverage?: number;
  current_mca_payments?: number;
  add_up_to_percentage?: number;
  true_monthly_revenue?: number;
  available_leverage?: number;
  offer_term_days?: number;
  offer_factor?: number;
}

export interface RevenueAnalysis {
  monthly_revenue: Record<string, number>;
  average_monthly_revenue: number;
  true_monthly_revenue: number;
  revenue_trend: "increasing" | "decreasing" | "stable";
}

export interface BalanceAnalysis {
  average_daily_balance: number;
  negative_balance_days: number;
  low_balance_days: number;
  min_balance: number;
  max_balance: number;
}

export interface MCAAnalysis {
  has_mca_history: boolean;
  mca_payments_found: number;
  payment_frequency: "daily" | "weekly" | "monthly" | "none";
  current_positions: number;
  estimated_payout: number;
}

export interface UnderwritingState {
  decisions: UnderwritingDecision[];
  currentDecision: UnderwritingDecision | null;
  loading: boolean;
  error: string | null;
  pagination: {
    count: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  };
  assessments: UnderwritingAssessment[];
  currentAssessment: UnderwritingAssessment | null;
  assessmentsPagination: {
    count: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  };
}

// =============================================================================
// STRUCTURED ANALYSIS OUTPUT TYPES (for frontend rendering)
// =============================================================================

export interface PositionSummary {
  // Core fields
  name: string;
  /** Advance vs line of credit. Absent on MoneyThumb output, which is all MCA. */
  position_type?: PositionKind;
  periodic_payment: number;
  /**
   * "unknown" covers everything outside the four schedules — a single pull, or
   * repeat pulls fitting none of them. Those stay out of leverage until someone
   * resolves the cadence.
   */
  payment_frequency: "day" | "week" | "biweek" | "month" | "unknown";
  status: string;
  flags: string[];

  // Financial
  funding_amount?: number;
  monthly_payout?: number;
  term_days?: number;

  // Dates
  first_payment_date?: string;
  last_payment_date?: string;
  funding_date?: string;

  // Payment stats
  total_payments_count?: number;
  cleared_payments_count?: number;
  nsf_count?: number;
  bounce_rate?: number;
  avg_delta?: number;

  // Metadata
  include_in_leverage: boolean;
  is_outlier: boolean; // True if excluded from leverage (no deposit, no special tags, not in first month)
  renewed_from?: string;
  detection?: string;

  // Account info (multi-account support)
  account_name?: string; // Bank account name (e.g., "Chase")
  account_number?: string; // Masked account number (e.g., "xxxx5350")

  /** Underwriter corrections: whether any apply, the note per field, and the key to post more. */
  overridden?: boolean;
  override_notes?: Record<string, string>;
  override_key?: string;
}

export interface MonthGroup {
  month_label: string;
  positions: PositionSummary[];
}

export interface RevenueLine {
  month_abbrev: string;
  amount: number;
  amount_display: string;
}

export interface OfferSummary {
  add_up_to_pct: number;
  daily_payment: number;
  total_amount: number;
  total_display: string;
}

export interface FunderDeposit {
  date: string;
  amount: number;
  /** Analysis row index of the deposit, for group click-filtering. */
  index?: number | null;
}

/**
 * A funder deposit that was money coming back out of a schedule (a bounced or
 * refunded pull, an exact multiple of the payment) rather than new funding.
 */
export interface FunderReturn {
  date: string;
  amount: number;
  index?: number | null;
}

/** Cadences a reviewer can set on a position, including reverting to unknown. */
export const RESOLVABLE_CADENCES = ["daily", "weekly", "biweekly", "monthly", "unknown"] as const;
export type ResolvableCadence = (typeof RESOLVABLE_CADENCES)[number];

/** Merchant cash advance vs line of credit vs term loan; LOC/loan share a dashboard section. */
export type PositionKind = "mca" | "loc" | "loan";

export interface FunderWithdrawal {
  amount: number;
  count: number;
  frequency: string;
  /**
   * Monthly obligation as the backend computed it. Present on EXP-tab output;
   * required for an unknown cadence, whose payout is an observed rate with no
   * multiplier to derive it from.
   */
  monthly_payout?: number;
  is_active: boolean;
  /**
   * Whether the backend counted this position in leverage. Deliberately separate
   * from is_active, which drives the status dot: an underwriter's correction can
   * exclude a position that is still visibly pulling, or include one we read as
   * settled.
   */
  include_in_leverage?: boolean;
  /**
   * How this schedule relates to the funder's other schedules. The Claude
   * pipeline emits at most one of "Renewed" | "Add-on" | "Adjusted"; MoneyThumb
   * output carries its own "--Flag" list. Rendered as a chip on the card.
   */
  tags: string[];
  /**
   * The key a correction on this position is posted under (pipeline and legacy MoneyThumb).
   */
  override_key?: string;
  /** ISO date of the first / last observed pull. Claude pipeline only. */
  first_date?: string | null;
  last_date?: string | null;
  /**
   * ISO date of the funder deposit this schedule repays (the latest deposit on
   * or before its first pull), or null when the pulls predate every observed
   * deposit. Absent entirely on MoneyThumb output and on runs persisted before
   * it existed.
   */
  funding_date?: string | null;
  /** Analysis row indices of the pulls behind this schedule. */
  indices?: number[];
  /** Funder deposits that came back out of this schedule. */
  returns?: FunderReturn[];
}

export interface FunderGroup {
  name: string;
  /** Absent on MoneyThumb output, which only ever reports MCA activity. */
  position_type?: PositionKind;
  is_active: boolean;
  total_deposits: number;
  total_withdrawn: number;
  deposits: FunderDeposit[];
  withdrawals: FunderWithdrawal[];
  /**
   * ISO date of the funder's most recent observed payment. Drives the
   * newest-first order of the Existing Positions panel. Emitted by the Claude
   * pipeline only; absent on MoneyThumb output and on runs persisted before it
   * existed, which keep their stored order.
   */
  last_payment_date?: string | null;
}

export interface CalendarTransaction {
  date: string;
  amount: number;
  /** "return" is a funder credit that came back out of a schedule, not funding. */
  tx_type: "deposit" | "withdrawal" | "return";
  funder: string;
}

export interface RevenueBridgeBucket {
  reason: string;
  amount: number;
  count: number;
}

export interface RevenueBridgeMonth {
  month: string;
  month_label: string;
  /** The bank's own printed credit total; null when a statement did not print one. */
  printed_credits: number | null;
  transcribed_credits: number;
  excluded: RevenueBridgeBucket[];
  true_revenue: number;
  is_full_month: boolean;
}

export interface RevenueByAccount {
  account: string;
  bank_name: string;
  account_number: string;
  true_revenue: number;
  true_revenue_display: string;
  deductions: number;
  deductions_display: string;
  /** Percent of run-level true revenue, one decimal. */
  share_of_revenue: number;
}

export interface AnalysisOutput {
  // Header section
  current_leverage_pct: number;
  monthly_payout: number;
  monthly_payout_display: string;
  status: string;
  decline_reasons: string[];
  review_flags: string[];
  offer?: OfferSummary;

  // Revenue section
  revenue_lines: RevenueLine[];
  /** Last 3 available completed months (excludes current month); used for leverage/offer. */
  average_revenue: number;
  average_revenue_display: string;
  /** Mean over all completed full months before current month (reporting). */
  average_revenue_all_full_months?: number;
  average_revenue_all_full_months_display?: string;
  /** YYYY-MM gaps in the last 3 calendar months (warning only; avg uses last 3 available). */
  missing_avg_revenue_months?: string[];

  // Positions section
  existing_positions: PositionSummary[];
  positions_by_month: MonthGroup[];
  outliers_by_month: MonthGroup[]; // Positions without deposits, not in first month

  // Footer section
  ordinal_position: string;
  active_position_count: number;
  /** Null when no trustworthy balance source exists (balance_source "unavailable"). */
  avg_daily_balance?: number | null;
  balance_line: string;
  /**
   * Balance provenance, carried in this payload rather than as a new prop
   * because the dashboard is also used for legacy MoneyThumb decisions. When it is
   * "unavailable" the figures above are deliberately absent rather than
   * estimated from statements that do not report balances; the backend logs
   * that per run instead of the UI explaining it.
   */
  balance_source?: "printed" | "printed_daily_summary" | "computed_reconciled" | "unavailable";
  negative_days?: number | null;
  low_days?: number | null;
  completeness_status?: "verified" | "delta" | "unavailable" | "not_applicable";
  /** Printed credits -> transcribed credits -> each exclusion bucket -> true revenue. */
  revenue_bridge?: RevenueBridgeMonth[];
  /** True revenue and exclusions split by account. Present when the run has more than one. */
  revenue_by_account?: RevenueByAccount[];

  // Summary stats
  total_positions_count: number;
  total_nsf_count: number;
  overall_bounce_rate: number;

  // Context
  statement_start_date?: string;
  statement_end_date?: string;
  months_of_history: number;
  business_name: string;

  // Balance chart data
  daily_balances?: Array<{ date: string; balance: number }>;

  // Funder-grouped data (dashboard view)
  funder_groups?: FunderGroup[];
  revenue_sources?: string[];
  calendar_transactions?: CalendarTransaction[];
}

// =============================================================================
// UNDERWRITER ASSESSMENT FORM TYPES
// =============================================================================

export type AssessmentPositionType = "loc" | "mca" | "recovery";
export type PullFrequency = "daily" | "weekly" | "monthly";
export type PositionTag =
  | "new"
  | "renewal"
  | "renewed"
  | "add-on"
  | "adjustment"
  | "adjusted"
  | "paid-in-full";
export type BalanceQuality = "good" | "okay" | "bad" | "fluctuating" | "not-available" | "";

export const DENY_REASONS = [
  "Over Leveraged",
  "Bad Balances",
  "Insufficient Revenue",
  "Too Many Positions",
  "Reverse Consolidation",
  "Debt Recovery/Collection",
  "Default History",
  "Stopped Payments",
  "Too Many Bounces",
  "Fraud",
  "Adjustments",
  "Poor Receivables",
  "Ineligible State (TX)",
  "No MCA History",
  "Other",
] as const;

export type DenyReason = (typeof DENY_REASONS)[number];

export interface AssessmentPosition {
  id: string;
  funder: string;
  fundingAmount: string;
  payment: number;
  frequency: PullFrequency;
  tag: PositionTag;
  type: AssessmentPositionType;
  active: boolean;
  month?: string; // Month abbreviation (e.g., 'Jan', 'Feb', 'Mar')
  notes?: string;
}

export interface UnderwriterAssessmentFormData {
  id?: number; // Optional for edit mode
  brokerCompany: string;
  brokerName: string;
  notes: string;
  defaultHistory: string;
  nMonths: number;
  statements: number[];
  positions: AssessmentPosition[];
  quality: BalanceQuality;
  accepted: boolean | null;
  fundingAmount?: number;
  addedLeverage?: number;
  merchantInstallment?: number;
  factor?: number;
  /** Percent points (e.g. 13 = 13%), 0–25; persisted on assessment when contract is sent */
  originationFeePercentage?: number | null;
  term?: number;
  frequency?: PullFrequency;
  denyReason?: DenyReason[];
  denyMoreInfo?: string;
  sources?: string;
  borrower?:
    | number
    | {
        id: number;
        company_name: string;
        owner_name: string;
      }; // For API requests, send just the ID (number). Object format reserved for future pre-filling use.
}

export interface UnderwritingAssessment extends UnderwriterAssessmentFormData {
  id: number; // Required for saved assessments
  created_at: string;
  updated_at: string;
  borrower?: {
    id: number;
    company_name: string;
    owner_name: string;
  };
  merchant_installment?: number;
}

export interface AssessmentState {
  currentAssessment: UnderwriterAssessmentFormData | null;
  submitting: boolean;
  submitError: string | null;
}

/** Refinance tab's calculator terms (`RefinanceOffer`) — one mutable row per borrower.
 * Decimal fields may arrive as strings from DRF when COERCE_DECIMAL_TO_STRING is unset. */
export interface RefinanceOffer {
  id: number;
  borrower: number;
  refiAmount: string | number | null;
  balance: string | number | null;
  factor: string | number | null;
  merchantInstallment: string | number | null;
  originationFeePercentage: string | number | null;
  term: number | null;
  frequency: PullFrequency | "";
  created_at: string;
  updated_at: string;
}

export interface RefinanceOfferPayload {
  refiAmount?: number | null;
  balance?: number | null;
  factor?: number | null;
  merchantInstallment?: number | null;
  originationFeePercentage?: number | null;
  term?: number | null;
  frequency?: string;
  /** The offer this save was made over (its updated_at, "new" for none): a changed one is a 409. */
  updated_at?: string;
}
